export type ChildResolveOutcome = 'staged' | 'aborted' | 'missing'

export interface ChildTabLoadingProjectionSnapshot {
  /** 标签唯一标识（也是 fileId / tabId）。 */
  id: string
  /** 当前已知的导入模型语义；null 表示尚未从 cache 推导出具体类型。 */
  type: string | null | undefined
}

export interface ChildTabLoadingCoordinatorOptions {
  /** 通过标准化后的引用 key（uuid 或 fileId 兜底 key）找到对应的 child tabId。 */
  resolveTabIdByUuid: (normalizedUuid: string) => string | undefined
  /** 读取当前标签快照，用于判断该 child 是否已经有已知类型。 */
  getTabSnapshotById: (tabId: string) => ChildTabLoadingProjectionSnapshot | undefined
  /** 宿主层真正执行 per-tab spinner 翻转的出口。 */
  setTabLoading: (tabId: string, loading: boolean) => void
  /** 错误上报出口，避免协调器内部直接依赖 logger。 */
  reportError: (message: string, error: unknown) => void
}

/**
 * `ChildTabLoadingCoordinator`
 *
 * 负责收口 imported-model child tab 的 spinner 生命周期。
 *
 * 设计目标：
 * 1. 把“缓存语义投影（type/icon）”与“请求生命周期结算（loading）”拆开；
 * 2. 对同一 child uuid 的并发解析使用 refcount，避免晚到 complete 提前关掉 spinner；
 * 3. 当 child 尚无已知类型时，把 `'staged'` 完成延后到 cache 投影之后再清 spinner，
 *    从而让 icon 回填与 spinner 归零在用户视角上保持同拍；
 * 4. 当 child 已有已知类型时，不再依赖无关 subscription 来结算 spinner。
 */
export class ChildTabLoadingCoordinator {
  private readonly options: ChildTabLoadingCoordinatorOptions
  private readonly refcountByUuid = new Map<string, number>()
  private readonly pendingProjectionTabIds = new Set<string>()

  constructor(options: ChildTabLoadingCoordinatorOptions) {
    this.options = options
  }

  /**
   * child 解析开始：首个 in-flight 请求点亮对应 tab 的 spinner。
   */
  handleResolveStart(uuid: string): void {
    const normalizedUuid = this.normalizeUuid(uuid)
    const previousCount = this.refcountByUuid.get(normalizedUuid) ?? 0
    this.refcountByUuid.set(normalizedUuid, previousCount + 1)
    if (previousCount > 0) {
      return
    }

    const tabId = this.options.resolveTabIdByUuid(normalizedUuid)
    if (!tabId) {
      return
    }
    this.setTabLoadingSafely(tabId, true, 'handleResolveStart setTabLoading failed')
  }

  /**
   * child 解析结束：根据 outcome 决定立即结算，还是等待 cache 投影完成后再结算。
   */
  handleResolveComplete(uuid: string, outcome: ChildResolveOutcome): void {
    const normalizedUuid = this.normalizeUuid(uuid)
    const previousCount = this.refcountByUuid.get(normalizedUuid) ?? 0
    if (previousCount > 1) {
      this.refcountByUuid.set(normalizedUuid, previousCount - 1)
      return
    }
    this.refcountByUuid.delete(normalizedUuid)

    const tabId = this.options.resolveTabIdByUuid(normalizedUuid)
    if (!tabId) {
      return
    }

    if (outcome !== 'staged') {
      this.pendingProjectionTabIds.delete(tabId)
      this.setTabLoadingSafely(tabId, false, 'handleResolveComplete setTabLoading failed')
      return
    }

    const currentTab = this.options.getTabSnapshotById(tabId)
    if (currentTab?.type) {
      this.setTabLoadingSafely(tabId, false, 'handleResolveComplete setTabLoading failed')
      return
    }

    this.pendingProjectionTabIds.add(tabId)
  }

  /**
   * cache subscription 投影完成后，检查哪些 child tab 已经从 unknown/null 解析出具体类型，
   * 并将这些“等待 publish 同拍结算”的 spinner 清零。
   */
  reconcileAfterCacheProjection(
    previousTabs: readonly ChildTabLoadingProjectionSnapshot[],
    nextTabs: readonly ChildTabLoadingProjectionSnapshot[],
  ): void {
    if (this.pendingProjectionTabIds.size === 0) {
      return
    }

    const previousTabById = new Map(previousTabs.map((tab) => [tab.id, tab]))
    const nextTabById = new Map(nextTabs.map((tab) => [tab.id, tab]))

    Array.from(this.pendingProjectionTabIds).forEach((tabId) => {
      const nextTab = nextTabById.get(tabId)
      if (!nextTab) {
        this.pendingProjectionTabIds.delete(tabId)
        return
      }

      const previousType = previousTabById.get(tabId)?.type ?? null
      const nextType = nextTab.type ?? null
      if (!previousType && nextType) {
        this.pendingProjectionTabIds.delete(tabId)
        this.setTabLoadingSafely(tabId, false, 'reconcileAfterCacheProjection setTabLoading failed')
      }
    })
  }

  /**
   * 组件卸载时清理内部状态，防止晚到请求继续持有旧引用。
   */
  reset(): void {
    this.refcountByUuid.clear()
    this.pendingProjectionTabIds.clear()
  }

  private normalizeUuid(uuid: string): string {
    return (uuid ?? '').trim().toLowerCase()
  }

  private setTabLoadingSafely(tabId: string, loading: boolean, message: string): void {
    try {
      this.options.setTabLoading(tabId, loading)
    } catch (error) {
      this.options.reportError(message, error)
    }
  }
}
